/**
 * WidgetCatalog Component
 * Sidebar/modal for browsing and adding widgets to the dashboard
 */

import { useState } from 'react';
import { cn } from '../../../lib/utils';
import { Button } from '../../ui/button';
import { Input } from '../../ui/input';
import { ScrollArea } from '../../ui/scroll-area';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
} from '../../ui/sheet';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../ui/tabs';
import { Badge } from '../../ui/badge';
import {
  Search,
  Plus,
  LayoutGrid,
  FileText,
  ListTodo,
  Monitor,
} from 'lucide-react';
import {
  WIDGET_CATALOG,
  WIDGET_CATEGORIES,
  getWidgetsByCategory,
  createWidgetInstance,
} from './widgetConfig';

export const WidgetCatalog = ({
  open,
  onOpenChange,
  onAddWidget,
  existingWidgets = [],
}) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [activeCategory, setActiveCategory] = useState('metrics');

  // Filter widgets by search query
  const filterWidgets = (widgets) => {
    if (!searchQuery.trim()) return widgets;
    const query = searchQuery.toLowerCase();
    return widgets.filter(
      (w) =>
        w.name.toLowerCase().includes(query) ||
        w.description.toLowerCase().includes(query)
    );
  };

  // Check if a widget type is already added
  const isWidgetAdded = (widgetType) => {
    return existingWidgets.some((w) => w.type === widgetType);
  };

  // Handle adding a widget
  const handleAddWidget = (widgetType) => {
    const instance = createWidgetInstance(widgetType);
    if (instance && onAddWidget) {
      onAddWidget(instance);
    }
  };

  // Category icons
  const getCategoryIcon = (categoryId) => {
    switch (categoryId) {
      case 'metrics':
        return <LayoutGrid className="w-4 h-4" />;
      case 'content':
        return <FileText className="w-4 h-4" />;
      case 'productivity':
        return <ListTodo className="w-4 h-4" />;
      case 'embeds':
        return <Monitor className="w-4 h-4" />;
      default:
        return <LayoutGrid className="w-4 h-4" />;
    }
  };

  // Render widget card
  const WidgetCard = ({ widget }) => {
    const Icon = widget.icon;
    const isAdded = isWidgetAdded(widget.id);

    return (
      <div
        className={cn(
          "p-4 rounded-lg border border-border/50 bg-card/50 hover:bg-card/80 transition-colors",
          isAdded && "opacity-60"
        )}
        data-testid={`catalog-widget-${widget.id}`}
      >
        <div className="flex items-start gap-3">
          <div className="p-2 rounded-lg bg-primary/10 flex-shrink-0">
            {Icon && <Icon className="w-5 h-5 text-primary" />}
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 mb-1">
              <h4 className="font-medium text-sm truncate" style={{ fontFamily: 'Chivo, sans-serif' }}>
                {widget.name}
              </h4>
              {isAdded && (
                <Badge variant="secondary" className="text-xs">
                  Added
                </Badge>
              )}
            </div>
            <p className="text-xs text-muted-foreground line-clamp-2">
              {widget.description}
            </p>
          </div>
        </div>
        <div className="mt-3 flex items-center justify-between">
          <div className="flex items-center gap-2 text-xs text-muted-foreground">
            <span>
              {widget.defaultSize.w}x{widget.defaultSize.h}
            </span>
            {widget.requiresApi && (
              <Badge variant="outline" className="text-xs">
                Live Data
              </Badge>
            )}
          </div>
          <Button
            size="sm"
            variant={isAdded ? "secondary" : "default"}
            onClick={() => handleAddWidget(widget.id)}
            className="h-7 px-2 gap-1"
            data-testid={`add-widget-${widget.id}`}
          >
            <Plus className="w-3.5 h-3.5" />
            {isAdded ? 'Add Another' : 'Add'}
          </Button>
        </div>
      </div>
    );
  };

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="right" className="w-full sm:w-[480px] p-0 flex flex-col">
        <SheetHeader className="px-6 py-4 border-b border-border/50">
          <SheetTitle className="flex items-center gap-2" style={{ fontFamily: 'Chivo, sans-serif' }}>
            <LayoutGrid className="w-5 h-5 text-primary" />
            Widget Catalog
          </SheetTitle>
          <SheetDescription>
            Browse and add widgets to customize your dashboard
          </SheetDescription>
        </SheetHeader>

        {/* Search */}
        <div className="px-6 py-3 border-b border-border/50">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <Input
              placeholder="Search widgets..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-9"
              data-testid="widget-search"
            />
          </div>
        </div>

        {/* Categories Tabs */}
        <Tabs
          value={activeCategory}
          onValueChange={setActiveCategory}
          className="flex-1 flex flex-col overflow-hidden"
        >
          <TabsList className="mx-6 mt-3 grid grid-cols-4 h-10">
            {WIDGET_CATEGORIES.map((cat) => (
              <TabsTrigger
                key={cat.id}
                value={cat.id}
                className="gap-1 text-xs px-2"
                data-testid={`category-tab-${cat.id}`}
              >
                {getCategoryIcon(cat.id)}
                <span className="hidden sm:inline">{cat.name.split(' ')[0]}</span>
              </TabsTrigger>
            ))}
          </TabsList>

          <ScrollArea className="flex-1 px-6 py-4">
            {WIDGET_CATEGORIES.map((cat) => (
              <TabsContent
                key={cat.id}
                value={cat.id}
                className="mt-0 space-y-3"
              >
                <p className="text-xs text-muted-foreground mb-4">
                  {cat.description}
                </p>
                <div className="space-y-3">
                  {filterWidgets(getWidgetsByCategory(cat.id)).map((widget) => (
                    <WidgetCard key={widget.id} widget={widget} />
                  ))}
                  {filterWidgets(getWidgetsByCategory(cat.id)).length === 0 && (
                    <div className="text-center py-8 text-muted-foreground">
                      <Search className="w-8 h-8 mx-auto mb-2 opacity-50" />
                      <p className="text-sm">No widgets found</p>
                    </div>
                  )}
                </div>
              </TabsContent>
            ))}
          </ScrollArea>
        </Tabs>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-border/50 bg-card/50">
          <p className="text-xs text-muted-foreground text-center">
            Drag widgets to reposition. Resize from corners.
          </p>
        </div>
      </SheetContent>
    </Sheet>
  );
};

export default WidgetCatalog;
